import { Button, cn, Paragraph } from '@quickmd-llc/design-system'
import { useEffect, useMemo, useRef } from 'react'
import {
  getGetPatientsPatientIdAlertsQueryKey,
  useGetPatientsPatientIdAlerts,
  usePutPatientsPatientIdAlertsMarkasread,
} from 'api/generated/alert/alert'
import { getGetPatientsPatientIdQueryKey } from 'api/generated/patient/patient'
import type { PatientAlertStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { client } from 'api/components/queryClient'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { ALERTS_SECTIONS_EMPTY_TEXT } from 'utils/constants'
import { VisitAlertCard } from './VisitAlertCard'
import { VisitSidePanel } from './VisitSidePanel'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * Slides in over the patient-info column to show the patient's alerts during a visit, replacing the
 * old Alerts tile behaviour of opening the patient chart in a new tab (QMD-4717).
 *
 * Pinning and the per-alert actions menu are deliberately absent: both appear in the design but are
 * deferred until the clinician-facing chart redesign defines them.
 */
export const VisitAlertsPanel = ({ isOpen, onClose }: Props) => {
  const { patientWithVisitInfo } = useDailyVisit()
  const patientId = patientWithVisitInfo.PatientId

  const { data, isLoading, error, refetch } = useGetPatientsPatientIdAlerts(patientId || '', {
    query: { enabled: isOpen && Boolean(patientId) },
  })

  /*
   * Opening the patient chart used to mark these read — PatientChart dispatches
   * markAllUserAlertsAsRead once the alerts load. Replacing that redirect with this panel would
   * otherwise strand AlertSummary.HasUnread (and the chart's "New!" badge) set forever, so the
   * panel takes over the same job. Once per patient per mount; the patient query is invalidated
   * so the summary this reads from is refreshed rather than left stale.
   */
  const { mutate: markAllAsRead } = usePutPatientsPatientIdAlertsMarkasread()
  const markedPatientRef = useRef<string | null>(null)
  // Unread comes from the fetched alerts, not `AlertSummary.HasUnread`: that summary is the
  // visit-open snapshot and never refreshes, so it would miss anything added mid-visit.
  const hasUnread = data?.some((alert) => !alert.IsRead)
  useEffect(() => {
    if (!isOpen || !patientId || !hasUnread || error) return
    if (markedPatientRef.current === patientId) return
    markedPatientRef.current = patientId
    markAllAsRead(
      { patientId },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: getGetPatientsPatientIdQueryKey(patientId) })
          client.invalidateQueries({ queryKey: getGetPatientsPatientIdAlertsQueryKey(patientId) })
        },
      },
    )
  }, [isOpen, patientId, hasUnread, error, markAllAsRead])

  // A NaN from an unparseable timestamp would make the comparator inconsistent and scramble the
  // whole list, not just that row, so coerce it to the epoch (sorts last) like a null.
  const sortKey = (alert: PatientAlertStaffApiOutContract) => {
    const time = new Date(alert.UpdateDateTime ?? 0).getTime()
    return Number.isNaN(time) ? 0 : time
  }

  const alerts = useMemo(() => [...(data ?? [])].sort((a, b) => sortKey(b) - sortKey(a)), [data])

  /*
   * The count is only stated once it is known. While loading or after a failure `alerts` is empty,
   * and a header reading "Alerts (0)" over a failed fetch asserts the patient has none — the one
   * thing this panel must never say when it does not know.
   */
  const title = isLoading || error ? 'Alerts' : `Alerts (${alerts.length})`

  return (
    <VisitSidePanel isOpen={isOpen} onClose={onClose} title={title} bodyClassName={cn('p-0')}>
      {isLoading ?
        <Paragraph size='sm' className='px-sp-2 py-sp-2 text-text-light'>
          Loading…
        </Paragraph>
      : error ?
        <div className='flex flex-col items-start gap-sp-1 px-sp-2 py-sp-2'>
          <Paragraph size='sm' className='text-text-error'>
            Couldn&apos;t load alerts. This patient may still have some.
          </Paragraph>
          <Button priority='secondary' size='s' onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      : alerts.length === 0 ?
        <Paragraph size='sm' className='px-sp-2 py-sp-2 text-text-light'>
          {ALERTS_SECTIONS_EMPTY_TEXT}
        </Paragraph>
      : <div className='flex flex-col'>
          {alerts.map((alert, index) => (
            <VisitAlertCard key={alert.AlertId ?? `${alert.UpdateDateTime}-${index}`} alert={alert} />
          ))}
        </div>
      }
    </VisitSidePanel>
  )
}
